import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { requestMonitor } from './requestMonitor';
import React from 'react';
import Cookies from 'js-cookie';
// Lazy-resolved so the @supabase/ssr SDK only loads when a 401 actually
// triggers a token refresh. On the first request of an authenticated page
// this module only needs axios + Cookies; the SDK chunk is paid for on
// demand. DEL-771.
import { getSupabase } from '../supabaseClient';
import { getAuthHeaders } from './authHeaders';
import {
  CSRF_HEADER,
  captureCsrfToken,
  ensureCsrfToken,
  getCsrfToken,
  isStateChangingMethod,
} from './csrfToken';

// Extend the request config to include metadata
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    requestId: string;
    source: string;
  };
  _retry?: boolean;
  _retry431?: boolean;
  _retryCsrf?: boolean;
}

// Cookies the app cannot function without. Everything else is fair game
// to clear when the request header outgrows the limit (431): the cached
// `user` blob that 10+ server pages used to JSON.parse (now retired),
// referral/affiliate tracking, UTM persistence, experiment/session flags.
// Supabase-managed `sb-*` cookies hold the actual session and must stay.
const ESSENTIAL_COOKIES = new Set([
  'refresh_token',
  'csrf_secret',
]);

function isEssentialCookie(name: string): boolean {
  if (ESSENTIAL_COOKIES.has(name)) return true;
  if (name.startsWith('sb-')) return true; // Supabase auth cookies
  if (name.includes('auth')) return true;  // defensive: any future auth lib
  return false;
}

function clearNonEssentialCookies(): void {
  if (typeof document === 'undefined') return;
  // The cookie spec gives no API to list all cookies for a given path, only
  // to read specific ones by name. Read every cookie we know we ever set,
  // plus the small set of names commonly used by analytics/affiliate flows.
  const candidates = [
    'user',
    'affiliateReturnUrl',
    'user_signed_up_tracked',
    'affiliate_code',
    'ref',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'experiment_assigned',
    'persona',
    'pending_analytics_events',
    'pending_onboarding_steps',
  ];
  for (const name of candidates) {
    if (!isEssentialCookie(name)) {
      Cookies.remove(name);
    }
  }
}

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// Default axios timeout: 30s in dev to absorb Next.js Turbopack cold compiles
// on first hit to an API route, 10s in prod where responses are cached/warm.
// Individual callers can still override per-request.
const DEFAULT_AXIOS_TIMEOUT_MS = process.env.NODE_ENV === 'development' ? 30000 : 10000;

function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
}

async function handleTokenRefresh(): Promise<string | null> {
  // Only attempt token refresh in browser environment
  if (typeof window === 'undefined') {
    return null;
  }

  // DEL-892: only refresh if the browser actually holds a Supabase session.
  // Without this guard, any 401 on an unauthenticated page (e.g. the /me
  // probe that AuthContext fires before sign-in) loads the ~240 KB gz
  // @supabase/ssr chunk on first paint just to attempt a refresh that
  // cannot succeed - the email flow uses an HttpOnly cookie and the user
  // is not signed in via Supabase. Mirror the AuthContext / accessToken
  // localStorage probe so the SDK only loads when a Supabase session
  // actually exists in browser storage.
  let hasSupabaseSession = false;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        hasSupabaseSession = true;
        break;
      }
    }
  } catch {
    // localStorage can throw in private modes. Treat as no session.
  }
  if (!hasSupabaseSession) {
    return null;
  }

  if (isRefreshing) {
    return new Promise(resolve => {
      subscribeTokenRefresh((token: string) => resolve(token));
    });
  }

  isRefreshing = true;

  try {
    // Use refreshSession to force a token refresh instead of getSession
    // which only returns the cached session without refreshing
    const supabase = await getSupabase();
    const { data: { session }, error } = await supabase.auth.refreshSession();
    if (error || !session) {
      console.error('[MonitoredAxios] Session refresh failed:', error);
      isRefreshing = false;
      return null;
    }

    const newToken = session.access_token;
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    onTokenRefreshed(newToken);
    isRefreshing = false;
    return newToken;
  } catch (err) {
    console.error('[MonitoredAxios] Token refresh error:', err);
    isRefreshing = false;
    return null;
  }
}

// Create a monitored axios instance
export const createMonitoredAxios = (source: string = 'unknown'): AxiosInstance => {
  // withCredentials: true tells the browser to send cookies (including the
  // HttpOnly `auth_session` set by /api/auth/set-session) with every
  // request. Without this, the server middleware falls back to the
  // Authorization header or x-user-token, which the client only sets for
  // OAuth flows. Setting it per-instance (not just on the default) keeps
  // the cookie behavior consistent across all monitored call sites.
  const instance = axios.create({
    timeout: DEFAULT_AXIOS_TIMEOUT_MS,
    withCredentials: true,
  });

  // Inherit default headers from axios.defaults (including Authorization)
  instance.defaults.headers.common = { ...axios.defaults.headers.common };

  // Match the default axios behavior so unmonitored (legacy) axios calls
  // also send the HttpOnly cookie.
  axios.defaults.withCredentials = true;

  // Request interceptor
  instance.interceptors.request.use(
    (config: ExtendedAxiosRequestConfig) => {
      // Start timing the request
      const requestId = requestMonitor.startRequest(config.url || '', source);

      // Store the request ID in the config for later use
      config.metadata = { requestId, source };

      // Add auth headers (Authorization, X-Portfolio-ID, X-Impersonated-User-ID)
      const portfolioId = config.params?.portfolioId as string | undefined;
      const authHeaders = getAuthHeaders(portfolioId);
      Object.entries(authHeaders).forEach(([key, value]) => {
        config.headers.set(key, value);
      });

      // getAuthHeaders always sets Content-Type: application/json, and the loop
      // above applies it with .set(), overwriting whatever the call site chose.
      // For a FormData body that is wrong - the browser has to set
      // multipart/form-data itself so it can include the boundary.
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        config.headers.delete('Content-Type');
      }

      // Complete the server's double-submit CSRF check. Required on any
      // state-changing request that authenticates via an ambient cookie -
      // which, since the HttpOnly migration, is every mutation made after a
      // hard navigation (no in-memory bearer token to fall back on).
      //
      // The token is primed at module load and refreshed off every response,
      // so it is normally already cached and the request goes out with no
      // delay. Only on a cold miss (the first mutation after a hard
      // navigation, while the module-load prime is still in flight) do we
      // wait for it: sending without one cost a 403 round trip plus a retry,
      // which is slower than awaiting the prime, and every one of those 403s
      // landed in the browser console as a "Failed to load resource" error.
      if (isStateChangingMethod(config.method)) {
        const cached = getCsrfToken();
        if (cached) {
          config.headers.set(CSRF_HEADER, cached);
          return config;
        }
        return ensureCsrfToken().then((csrfToken) => {
          if (csrfToken) config.headers.set(CSRF_HEADER, csrfToken);
          return config;
        });
      }

      return config;
    },
    (error) => {
      console.error(`[RequestMonitor] ${source}: Request error`, error);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => {
      const config = response.config as ExtendedAxiosRequestConfig;
      const requestId = config.metadata?.requestId;
      const source = config.metadata?.source || 'unknown';

      // The server rotates the CSRF token on every response; keep the latest.
      captureCsrfToken(response.headers);

      if (requestId) {
        requestMonitor.endRequest(
          requestId,
          response.config.url || '',
          response.config.method || 'GET',
          response.status,
          false, // Not cached
          source,
          response.headers['content-length'] ? parseInt(String(response.headers['content-length'])) : undefined
        );
      }

      return response;
    },
    async (error: AxiosError) => {
      const config = error.config as ExtendedAxiosRequestConfig;
      const requestId = config?.metadata?.requestId;
      const source = config?.metadata?.source || 'unknown';

      // Error responses carry a fresh CSRF token too - notably the 403 that
      // rejects a stale one, which is exactly what the retry below needs.
      if (error.response?.headers) {
        captureCsrfToken(error.response.headers);
      }

      if (requestId) {
        requestMonitor.endRequest(
          requestId,
          error.config?.url || '',
          error.config?.method || 'GET',
          error.response?.status || 0,
          false, // Not cached
          source
        );
      }

      // Handle 401 errors with token refresh retry (browser only)
      if (error.response?.status === 401 && config && !config._retry && typeof window !== 'undefined') {
        config._retry = true;

        console.log(`[MonitoredAxios] ${source}: Received 401, attempting token refresh`);

        const newToken = await handleTokenRefresh();

        if (newToken) {
          console.log(`[MonitoredAxios] ${source}: Token refreshed, retrying request`);
          config.headers['Authorization'] = `Bearer ${newToken}`;

          try {
            const response = await instance(config);
            return response;
          } catch (retryError) {
            console.error(`[MonitoredAxios] ${source}: Retry failed:`, retryError);
            return Promise.reject(retryError);
          }
        } else {
          // Token refresh failed - let the error propagate to be handled by caller
          // DashboardContext and other contexts already handle 401 by signing out
          console.log(`[MonitoredAxios] ${source}: Token refresh failed, propagating error`);
          return Promise.reject(error);
        }
      }

      // Handle 403 from the double-submit CSRF check. The token rotates on
      // every response, so a mutation issued with one the server has already
      // moved past is rejected once. We captured the fresh token off this very
      // response above, so a single retry with it succeeds. Scoped to the CSRF
      // error so genuine authorization failures still propagate untouched.
      if (
        error.response?.status === 403 &&
        config &&
        !config._retryCsrf &&
        isStateChangingMethod(config.method) &&
        typeof window !== 'undefined' &&
        typeof (error.response.data as { error?: string })?.error === 'string' &&
        (error.response.data as { error: string }).error.toLowerCase().includes('csrf')
      ) {
        config._retryCsrf = true;

        const freshToken = getCsrfToken() ?? (await ensureCsrfToken());
        if (freshToken) {
          console.log(`[MonitoredAxios] ${source}: CSRF token rejected, retrying with a fresh one`);
          config.headers[CSRF_HEADER] = freshToken;
          try {
            return await instance(config);
          } catch (retryError) {
            return Promise.reject(retryError);
          }
        }
      }

      // Handle 431 (Request Header Fields Too Large) on same-origin API calls.
      // The browser usually refuses to send the request at all once the
      // Cookie header exceeds ~8 KB; 431 is the explicit "we told you so"
      // response. Clearing non-essential cookies (analytics, the retired
      // `user` blob, affiliate tracking, UTM) gives the retry a chance to
      // get under the limit. If the retry still 431s, we propagate - the
      // caller's UI has a real sign-out path now (DashboardClient).
      if (
        error.response?.status === 431 &&
        config &&
        !config._retry431 &&
        typeof window !== 'undefined'
      ) {
        config._retry431 = true;
        console.warn(`[MonitoredAxios] ${source}: Received 431, clearing non-essential cookies and retrying once`);

        clearNonEssentialCookies();
        try {
          const response = await instance(config);
          return response;
        } catch (retryError) {
          console.error(`[MonitoredAxios] ${source}: 431 retry failed:`, retryError);
          return Promise.reject(retryError);
        }
      }

      // ponytail: cancellations are not failures - the caller already moved
      // on (DashboardContext aborts in-flight /api/batch on every scenario
      // switch and treats axios.isCancel() as a no-op). Logging them here
      // sends a `console.error("Failed ... - Network Error")` to PostHog's
      // exception autocapture, which surfaces as `CanceledError: canceled`
      // with no [IN-APP] frames - noise that costs a real alert.
      //
      // DEL-1045: also drop the `Failed POST /api/analytics/event - 401`
      // console.error specifically. The trackEvent guard now short-circuits
      // anonymous callers before this code path, but anything that still
      // reaches the analytics endpoint while unauthenticated is by
      // definition expected behaviour, not a failure to alert on. Other 401s
      // (auth/me, financial endpoints) still surface - this only narrows
      // the known-noisy analytics path.
      const failedUrl = String(error.config?.url || '');
      const failedStatus = error.response?.status;
      const isExpectedAnalyticsEvent401 =
        failedStatus === 401 &&
        (failedUrl === '/api/analytics/event' || failedUrl.endsWith('/api/analytics/event'));
      if (!axios.isCancel(error) && !isExpectedAnalyticsEvent401) {
        console.error(`[RequestMonitor] ${source}: Failed ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status || 'Network Error'}`);
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Default monitored instance for general use
export const monitoredAxios = createMonitoredAxios('general');

// Helper function to create cached request logging
export const logCachedRequest = (url: string, method: string = 'GET', source: string = 'unknown') => {
  requestMonitor.logRequest({
    url,
    method,
    duration: 0, // Cached requests have no duration
    status: 200,
    cached: true,
    _source: source
  });
  
};

// Export a hook for easy usage in components
export const useMonitoredAxios = (source: string) => {
  return React.useMemo(() => createMonitoredAxios(source), [source]);
};
